import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../../components/ui/dropdown-menu'
import { DecorativeWave } from '../../../../components/ui/DecorativeWave'
import type { SectionHeaderProps } from './SectionHeader.types'
import './SectionHeader.scss'

/**
 * SectionHeader — Visual group divider in the project task list.
 *
 * Displays a section label with collapse toggle, options menu,
 * and a decorative wave SVG. Supports inline rename via double-click.
 *
 * Hover states (CSS-driven):
 * - Rest: only title + count + wave visible
 * - Hover: arrow slides in from left, title/options/wave shift right
 * - Collapsed: arrow always visible (section stays shifted)
 * - Arrow hover: arrow gets --space-700 bg
 *
 * @example
 * <SectionHeader
 *   id="abc"
 *   title="Backlog"
 *   taskCount={5}
 *   collapsed={false}
 *   onToggleCollapse={(id) => {}}
 *   onRename={(id, title) => {}}
 *   onDelete={(id) => {}}
 * />
 */
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  id,
  title,
  taskCount,
  collapsed,
  focused = false,
  selected = false,
  editing = false,
  autoEdit = false,
  dragListeners,
  dragAttributes,
  dragRef,
  onToggleCollapse,
  onRename,
  onDelete,
  onSelect,
  onSelectAllTasks,
  onEditEnd,
  onNavigateDown,
  onNavigateUp,
  readonly = false,
}) => {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(autoEdit)
  const [editValue, setEditValue] = useState(autoEdit ? '' : title)
  const [menuOpen, setMenuOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const isSubmittingRef = useRef(false)
  const cancelledRef = useRef(false)
  const editValueRef = useRef(editValue)
  editValueRef.current = editValue

  // Sync title prop into edit value (only when prop changes, not on edit toggle)
  useEffect(() => {
    setEditValue(title)
  }, [title])

  // Enter/exit edit mode driven by parent's editing prop
  useEffect(() => {
    if (editing && !isEditing) {
      setIsEditing(true)
    } else if (!editing && !autoEdit && isEditing) {
      setIsEditing(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing) {
      const input = inputRef.current
      if (input) {
        input.focus()
        // Place cursor at end instead of selecting all text
        const len = input.value.length
        input.setSelectionRange(len, len)
      }
    }
  }, [isEditing])

  const handleSubmit = useCallback(() => {
    if (isSubmittingRef.current || cancelledRef.current) return
    isSubmittingRef.current = true
    // Read from ref to guarantee latest value (avoids stale closure)
    const trimmed = editValueRef.current.trim()
    if (trimmed && trimmed !== title) {
      onRename(id, trimmed)
    } else if (!trimmed && !title) {
      // New section with no name entered — delete it (same as Escape)
      onDelete(id)
    } else if (!trimmed) {
      setEditValue(title)
    }
    setIsEditing(false)
    onEditEnd?.()
    requestAnimationFrame(() => {
      isSubmittingRef.current = false
      // Refocus the navigation wrapper so keyboard events continue working
      const sectionEl = document.querySelector(`[data-section-id="${id}"]`)
      const wrapper = sectionEl?.closest('[tabindex]') as HTMLElement
      wrapper?.focus({ preventScroll: true })
    })
  }, [title, id, onRename, onDelete, onEditEnd])

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      // New section with no saved title → cancel creation (delete)
      if (!title) {
        cancelledRef.current = true
        onDelete(id)
        return
      }
      setEditValue(title)
      setIsEditing(false)
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      handleSubmit()
      onNavigateDown?.()
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      handleSubmit()
      onNavigateUp?.()
    }
  }, [handleSubmit, title, id, onDelete, onNavigateDown, onNavigateUp])

  return (
    <div
      ref={readonly ? undefined : dragRef}
      className={`section-header${(focused || selected) ? ' section-header--focused' : ''}${readonly ? ' section-header--readonly' : ''}`}
      data-section-id={id}
      {...(!readonly && !isEditing ? dragAttributes : undefined)}
      {...(!readonly && !isEditing ? dragListeners : undefined)}
      onClick={(e) => {
        // Clicks on the wave area or empty space (not label/arrow/options) → select
        const target = e.target as HTMLElement
        if (target.closest('.section-header__label') || target.closest('.section-header__arrow') || target.closest('.section-header__options')) return
        onSelect?.(id)
      }}
      onDoubleClick={(e) => {
        const target = e.target as HTMLElement
        if (target.closest('.section-header__arrow') || target.closest('.section-header__options')) return
        onSelect?.(id)
        onSelectAllTasks?.(id)
      }}
    >
      {/* Collapse arrow — outside label so it sits further left */}
      <button
        className="section-header__arrow"
        onClick={() => onToggleCollapse(id)}
        type="button"
      >
        <svg
          className={`section-header__arrow-icon${collapsed ? ' section-header__arrow-icon--collapsed' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M4.5 6L8 9.5L11.5 6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className="section-header__label">
        {/* Title */}
        {isEditing ? (
          <div className="section-header__title-sizer" data-value={editValue || t('section.defaultName')}>
            <input
              ref={inputRef}
              className="section-header__title-input"
              value={editValue}
              placeholder={t('section.defaultName')}
              size={1}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onBlur={handleSubmit}
              spellCheck={false}
            />
          </div>
        ) : (
          <span
            className="section-header__title"
            onClick={() => onToggleCollapse(id)}
          >
            {editValue}
          </span>
        )}

        {/* Task count — hidden for empty sections */}
        {taskCount > 0 && <span className="section-header__count">{taskCount}</span>}
      </div>

      {/* Options (ellipsis) — hidden when readonly (e.g. virtual sections) */}
      {!readonly && (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button className={`section-header__options${menuOpen ? ' section-header__options--open' : ''}`} type="button">
              <MoreHorizontal size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={() => requestAnimationFrame(() => setIsEditing(true))}>
              {t('section.rename')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="context-menu-content__item--danger"
              onSelect={() => onDelete(id)}
            >
              {t('section.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Wave decoration — fills remaining width, clipped (not stretched). */}
      <DecorativeWave className="section-header__wave" />
    </div>
  )
}
